//-- Copyright (c) 2021 TMT Insights Group, LLC
import React from 'react';
import { convertStylesToStyled } from '@/_utils/utils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { TitleMap } from '@/constants/constants';
import { ConfigContext } from '@/contextAPI/configContext';
import styled from '@emotion/styled';
import { Box, Chip, CircularProgress, Fade, Grid, IconButton, Typography } from '@mui/material';
import { useContext } from 'react';
import { selectJob } from '../../../../redux/selectors/jobSelectors';
import ConditionalTooltip from '../../../_common/ConditionalTooltip/ConditionalTooltip';
import { useAppSelector } from '@/redux/hooks';

// for some clients, we don't want to show a proxy button at all, for which we use `hideProxyButton`
// the config setting `disableJobDetailProxy` will disable the proxy button
// the `disableProxyButton` prop is used to disable the button when there is no proxy video available

const JobTitleCardOverlay = ({ tmdbTitle, toggleVideo, disableProxyButton, hideProxyButton, checkingProxy }) => {
  const data = useAppSelector(selectJob);
  const { title = {} } = data;
  const {
    rating,
    release_year,
    genres,
    title_name,
    name,
    entity_type,
    episode_name,
    runtime,
    version_type,
    lineage_meta = {},
    sequence_order
  } = title;
  const override = useContext(ConfigContext)?.jobDetails?.titleCardOverlay?.(data);
  const titleName = lineage_meta.series_name || title_name || name || tmdbTitle?.name;
  const seasonNum = lineage_meta.season_sequence_order;
  const episodeNum = sequence_order;
  const episodeName = episode_name || (entity_type !== 'feature' && name);

  return (
    <Fade in timeout={600}>
      <div>
        {rating?.length && <Rating variant="h5">{rating}</Rating>}
        <BottomOverlay container justifyContent="space-between" alignItems="flex-end">
          <Box>
            <div>
              {entity_type && <TitleTypeChip variant="filled" color="default" label={entity_type} />}
              {!!genres?.length &&
                genres.map((genre) => <Chip key={genre} variant="filled" color="secondary" label={genre} sx={{ textTransform: 'capitalize' }} />)}
            </div>
            <Typography variant="h2" sx={{ mt: 1 }}>
              {titleName} {!!release_year && <YearChip size="small" variant="outlined" color="default" label={release_year} />}
            </Typography>
            <Meta>
              {override ? (
                override
              ) : (
                <>
                  {entity_type !== 'feature' && (
                    <>
                      {' '}
                      {!!seasonNum && <span>S{seasonNum || '?'}</span>}
                      {!!episodeNum && <span>E{episodeNum || '?'}</span>}
                      {!!episodeName && (
                        <strong>
                          <span>&quot;{episodeName}&quot;</span>
                        </strong>
                      )}
                    </>
                  )}
                  {!!version_type && <span>{version_type}</span>}
                  {!!runtime && <span>{runtime} mins</span>}
                </>
              )}
            </Meta>
          </Box>

          {!hideProxyButton && (
            <ConditionalTooltip show={!checkingProxy && disableProxyButton} title="No video currently available.">
              <div>
                <PlayButton disabled={disableProxyButton} onClick={toggleVideo} size="large">
                  {checkingProxy && <CircularProgress size={60} thickness={2} color="secondary" />}
                  <PolarisIcon icon="play_circle" height={50} />
                </PlayButton>
              </div>
            </ConditionalTooltip>
          )}
        </BottomOverlay>
      </div>
    </Fade>
  );
};

export default JobTitleCardOverlay;

const TitleTypeChip = styled(Chip)`
  text-transform: capitalize;
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.root)}
  ${({ theme, label }) => {
    const titleType = TitleMap[label];
    const bgColor = {
      feature: theme.palette.primary.main,
      series: theme.palette.secondary.main,
      season: theme.palette.tertiary.main,
      episode: theme.palette.teal.main,
      version: theme.palette.success.main
    };
    return convertStylesToStyled(theme.customOverrides?.TypeLabel?.[titleType]) || `background: ${bgColor[titleType]};`;
  }};
  .MuiChip-label {
    text-shadow: none;
  }
`;

const BottomOverlay = styled(Grid)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  flex-wrap: nowrap;

  .MuiBox-root {
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing(3)};
    word-break: break-word;
    color: #fff;

    .MuiChip-root {
      margin-right: ${({ theme }) => theme.spacing(0.5)};
      font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
      color: white;
      text-shadow: 0 0 5px black;
    }
  }
`;

const YearChip = styled(Chip)`
  border-radius: 5px;
  padding: ${({ theme }) => theme.spacing(0.5)};
  height: 25px;
`;

const Meta = styled.div`
  padding: ${({ theme }) => theme.spacing(1, 0, 0)};
  display: flex;
  align-items: center;

  span {
    opacity: 0.6;
    display: inline-flex;
    align-items: center;
    padding: ${({ theme }) => theme.spacing(0, 1, 0, 0)};

    &:not(:last-child):after {
      content: '';
      display: inline-block;
      width: 0.4rem;
      height: 0.4rem;
      border-radius: 50%;
      background: ${({ theme }) => theme.palette.text.primary};
      margin: ${({ theme }) => theme.spacing(0, 0, 0, 1)};
  }
`;

const PlayButton = styled(IconButton)`
  margin: ${({ theme }) => theme.spacing(-2, 2, 2, 2)};
  color: #fff;
  z-index: 99;
  &.Mui-disabled {
    color: #fff;
    .MuiSvgIcon-root {
      opacity: 0.33;
    }
  }
  &:hover {
    opacity: 0.8;
    transition: ease opacity 300ms;
  }
  .MuiSvgIcon-root {
    opacity: 1;
    transition: ease opacity 300ms;
    font-size: 4rem;
  }
  .MuiCircularProgress-root {
    position: absolute;
  }
`;

const Rating = styled(Typography)`
  background: ${({ theme }) => theme.palette.primary.dark}aa;
  padding: ${({ theme }) => theme.spacing(0.5, 1.5)};
  border-radius: 5px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  display: block;
  align-items: center;
  justify-content: center;
  margin: ${({ theme }) => theme.spacing(2)};
  position: absolute;
  right: 0;
  top: 0;
  color: white;
`;
